import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Box } from '@mui/material'
import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { useTranslation } from 'react-i18next'
import { ReportFormComponent } from '@/features/reports/components/report-form.tsx'
import { useForm } from 'react-hook-form'
import {
  fetchReport,
  type ReportImportForm,
  ReportImportFormSchema
} from '@/features/reports/api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useState } from 'react'
import { useReportEdit } from '@/features/reports/hooks/useReportEdit.tsx'
import type { FileId } from '@/types/file.types.ts'
import { useQuery } from '@tanstack/react-query'
import { useCompany } from '@/contexts'
import { QueryLoader } from '@/components/QueryLoader/QueryLoader.tsx'
import { useMessageSnackbar } from '@/hooks/useMessageSnackbar'

export const Route = createFileRoute(
  '/_authenticated/$companySlug/reports/edit/$reportId'
)({
  beforeLoad: ({ context }) => {
    context.auth.rejectIfMissingPermissions(['REPORTING_WRITE', 'REPORTING_READ'])
  },
  component: RouteComponent
})

function RouteComponent() {
  const { t } = useTranslation(['_routes', '_global'])
  const navigate = Route.useNavigate()
  const { reportId } = Route.useParams()
  const { id: companyId } = useCompany()
  const { showSuccess, showError } = useMessageSnackbar()

  const getReportQuery = useQuery({
    queryKey: ['report', reportId],
    queryFn: ({ signal }) => fetchReport(reportId!, signal),
    enabled: !!reportId
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors }
  } = useForm<ReportImportForm>({
    resolver: zodResolver(ReportImportFormSchema),
    defaultValues: {
      name: '',
      date: new Date(),
      files: [],
      siteIds: []
    }
  })

  // set form fields with fetched report
  useEffect(() => {
    if (getReportQuery.data) {
      reset({
        name: getReportQuery.data.name,
        date: new Date(getReportQuery.data.date),
        siteIds: getReportQuery.data.siteIds.map(s => s.toString()),
        files: getReportQuery.data.files
      })
    }
  }, [getReportQuery.data, reset])

  const [uploadProgress, setUploadProgress] = useState<Record<FileId, number>>({})

  // Progress callback
  const handleProgress = (fileId: FileId, progress: number) => {
    setUploadProgress(prev => ({
      ...prev,
      [fileId]: progress
    }))
  }

  // Mutation for uploading a single file
  const editMutation = useReportEdit(handleProgress)

  // Submit handler: upload files in parallel with progress tracking
  const onSubmit = useCallback(
    async (data: ReportImportForm) => {
      try {
        // Upload all files in parallel
        await editMutation.mutateAsync({
          companyId,
          reportId: reportId,
          file: data.files.find(it => it instanceof File)!,
          formData: data,
          onProgress: handleProgress
        })

        showSuccess(t('routes.reports_edit.main.messages.editSuccess'))
        navigate({
          to: '/$companySlug/reports'
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'unknown_error'
        const cause = error instanceof Error ? error.cause : 'An error as occured'
        showError(
          t(`_global:api.message.errors.${message}`, {
            defaultValue: cause as string
          })
        )
      }
    },
    [companyId, navigate, editMutation, reportId]
  )

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <PageHeader
        backArrow={{ historyBack: true }}
        subtitle={t('routes.reports_edit.main.subtitle')}
      >
        {t('routes.reports_edit.main.title')}
      </PageHeader>

      <QueryLoader query={getReportQuery}>
        {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
        {_ => (
          <ReportFormComponent
            control={control}
            errors={errors}
            isSubmitting={isSubmitting}
            mutation={editMutation}
            progress={uploadProgress}
            isEditMode={true}
          />
        )}
      </QueryLoader>
    </Box>
  )
}
